import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import "./richTextEditor.css";
import RenderedHtml from "../renderedHtml";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faAlignJustify,
} from "@fortawesome/free-solid-svg-icons";
import { FontSize, TextStyle } from "@tiptap/extension-text-style";
import { Paragraph } from "@tiptap/extension-paragraph";
import HardBreak from "@tiptap/extension-hard-break";
import { mergeAttributes } from "@tiptap/core";

const CustomParagraph = Paragraph.extend({
  name: "paragraph",
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { role: "paragraph" }), 0];
  },
});

export default function RichTextEditor({ content = "", onChange = () => {} }) {
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ paragraph: false }),
      CustomParagraph,
      Underline,
      TextStyle,
      FontSize,
      HardBreak,
      TextAlign.configure({
        types: ["paragraph"],
      }),
    ],
    content: content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div>
      {!isPreviewMode ? (
        <>
          <EditorContent className="rich-text-editor" editor={editor} />
          <div className="rich-text-editor-hint">
            <small>Użyj SHIFT+ENTER aby wstawić nowy akapit</small>
          </div>
          <div className="rich-text-editor-toolbar">
            <div className="toolbar-row">
              <div className="button-group formatting-group">
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  className={`rich-text-editor-button bold-button ${
                    editor?.isActive("bold") ? "is-active" : ""
                  }`}
                  title="Pogrubienie"
                >
                  B
                </button>
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  className={`rich-text-editor-button italic-button ${
                    editor?.isActive("italic") ? "is-active" : ""
                  }`}
                  title="Kursywa"
                >
                  I
                </button>
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  className={`rich-text-editor-button underline-button ${
                    editor?.isActive("underline") ? "is-active" : ""
                  }`}
                  title="Podkreślenie"
                >
                  U
                </button>
              </div>
              <div className="button-group alignment-group">
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().setTextAlign("left").run()
                  }
                  className={`rich-text-editor-button align-button ${
                    editor?.isActive({ textAlign: "left" }) ? "is-active" : ""
                  }`}
                  title="Wyrównaj do lewej"
                >
                  <FontAwesomeIcon icon={faAlignLeft} />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().setTextAlign("center").run()
                  }
                  className={`rich-text-editor-button align-button ${
                    editor?.isActive({ textAlign: "center" }) ? "is-active" : ""
                  }`}
                  title="Wyśrodkuj"
                >
                  <FontAwesomeIcon icon={faAlignCenter} />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().setTextAlign("right").run()
                  }
                  className={`rich-text-editor-button align-button ${
                    editor?.isActive({ textAlign: "right" }) ? "is-active" : ""
                  }`}
                  title="Wyrównaj do prawej"
                >
                  <FontAwesomeIcon icon={faAlignRight} />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().setTextAlign("justify").run()
                  }
                  className={`rich-text-editor-button align-button ${
                    editor?.isActive({ textAlign: "justify" })
                      ? "is-active"
                      : ""
                  }`}
                  title="Wyjustuj"
                >
                  <FontAwesomeIcon icon={faAlignJustify} />
                </button>
              </div>
              <div className="button-group font-size-group">
                <button
                  type="button"
                  onClick={() =>
                    editor
                      .chain()
                      .focus()
                      .setMark("textStyle", { fontSize: "12px" })
                      .run()
                  }
                  title="Mały rozmiar czcionki"
                  className="rich-text-editor-button font-size-button small-font-button"
                >
                  A
                </button>
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().unsetMark("textStyle").run()
                  }
                  title="Normalny rozmiar czcionki"
                  className="rich-text-editor-button font-size-button regular-font-button"
                >
                  A
                </button>
                <button
                  type="button"
                  onClick={() =>
                    editor
                      .chain()
                      .focus()
                      .setMark("textStyle", { fontSize: "24px" })
                      .run()
                  }
                  title="Duży rozmiar czcionki"
                  className="rich-text-editor-button font-size-button large-font-button"
                >
                  A
                </button>
              </div>
            </div>
            <div className="toolbar-row">
              <div className="button-group list-group">
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().toggleBulletList().run()
                  }
                  className={`rich-text-editor-button list-button ${
                    editor.isActive("bulletList") ? "is-active" : ""
                  }`}
                  title="Lista punktowana"
                >
                  • Lista
                </button>
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().toggleOrderedList().run()
                  }
                  className={`rich-text-editor-button list-button ${
                    editor.isActive("orderedList") ? "is-active" : ""
                  }`}
                  title="Lista numerowana"
                >
                  1. Numery
                </button>
              </div>
              <div className="button-group preview-group">
                <button
                  type="button"
                  onClick={() => setIsPreviewMode(true)}
                  className="rich-text-editor-button preview-button"
                >
                  Zobacz pogląd
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="rich-text-preview">
          <div className="rich-text-preview-header">
            <h3>Pogląd treści</h3>
          </div>
          <div className="rich-text-preview-content">
            <RenderedHtml html={editor.getHTML()} />
          </div>
          <div className="rich-text-preview-footer">
            <button
              type="button"
              onClick={() => setIsPreviewMode(false)}
              className="rich-text-editor-button back-to-edit-button"
            >
              Powrót do edycji
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
